import { store } from '../../../store'
import { voohCampaignFormActions } from './slice'
import { User, UserSortColumn, UserStatus } from '../../../types/user'
import { CardStatus } from '../../../components/Layout/Card'
import api from '../../../api'
import {
  VoohBasicFormErrors,
  VoohBasicFormErrorsEmpty,
  VoohBrandNetwork,
  VoohCampaignBasicValues,
  VoohCampaignForm,
  VoohCreation,
  VoohCreationUpdate,
  VoohMedia,
  VoohMediaFormat,
  VoohMediaNode,
  VoohMediaTemplate,
  VoohSourceMediaFormat,
  VoohUsedMedia,
} from './types'
import { SelectAsyncValue } from '../../../components/Form/SelectAsync'
import { Company } from '../../../api/companies/types'
import { Agencies } from '../../Campaign/CampaignForm/BasicsForm/types'
import { AGENCIES_LIMIT, INPUT_LIST_LIMIT, VOOH_CAMPAIGN_MAX_NAME_LENGTH } from '../../../constant'
import { Agency } from '../../../types/agency'
import { MutationStateResponse, SortDirection } from '../../../types/various'
import { userFullName } from '../../../utils/user'
import { TFunction } from 'i18next'
import VariableUtils from '../../../utils/variable'
import ErrorUtils from '../../../utils/error'
import DateUtils from '../../../utils/date'
import { UseAuthorizeProps } from '../../../hooks/useAuthorize'
import { CAMPAIGN_FORM } from '../../../constant/authorization'
import { CampaignStatus } from '../../../types/campaign'
import { Routes } from '../../../routes'
import { NavigateFunction } from 'react-router-dom'
import { voohCampaignRealizationActions } from '../VoohCampaignDetails/VoohCampaignRealization/slice'
import { CreateVoohMediaTemplateFR } from '../../../api/voohCampaign/types'
import { FetchResult } from '@apollo/client'
import { ArchiveVoohCreationFR } from '../../../api/voohCampaign/voohCreation/types'

export default class VoohCampaignFormService {
  static submitBasicValues = (
    currentVoohState: VoohCampaignForm,
    navigate: NavigateFunction
  ): Promise<void> => {
    store.dispatch(voohCampaignFormActions.setCardStatus({ basics: CardStatus.SAVING }))
    const {
      agency,
      name,
      briefName,
      startDate,
      endDate,
      supervisors,
      advertiser,
      clubMediaSource,
    } = currentVoohState.baseValues
    if (currentVoohState.id) {
      return api.voohCampaign
        .updateVoohCampaign({
          id: currentVoohState.id,
          agencyId: agency ? agency!.rawData!.id : null,
          advertiserId: advertiser ? advertiser!.rawData!.id : null,
          name,
          briefName,
          startDate,
          endDate,
          supervisors: supervisors.map(supervisor => supervisor.rawData!.id),
          sourceMedia: clubMediaSource?.rawData?.type || VoohSourceMediaFormat.MOVE,
          brandId:
            clubMediaSource?.rawData?.type === VoohSourceMediaFormat.BRAND
              ? clubMediaSource?.rawData?.id
              : undefined,
          networkId:
            clubMediaSource?.rawData?.type === VoohSourceMediaFormat.NETWORK
              ? clubMediaSource?.rawData?.id
              : undefined,
        })
        .then(res => {
          if (!res.data) {
            return
          }

          store.dispatch(
            voohCampaignFormActions.setVoohCampaignPublishable(
              res.data.updateVoohCampaign.voohCampaign.publishable
            )
          )

          if (
            !VariableUtils.isDeepEqual(
              res.data.updateVoohCampaign.voohCampaign.mediaTree,
              currentVoohState.mediums
            )
          ) {
            store.dispatch(
              voohCampaignFormActions.setVoohMedia(
                res.data.updateVoohCampaign.voohCampaign.mediaTree
              )
            )

            const selectedVoohMediaNodes: VoohMediaNode[] = []
            JSON.stringify(res.data.updateVoohCampaign.voohCampaign.mediaTree, (_, nestedValue) => {
              if (nestedValue?.address) {
                selectedVoohMediaNodes.push(nestedValue)
              }
              return nestedValue
            })
            store.dispatch(voohCampaignFormActions.setSelectedVoohMedia(selectedVoohMediaNodes))
          }

          store.dispatch(voohCampaignFormActions.setCardStatus({ basics: CardStatus.SAVED }))
        })
        .catch(
          () =>
            void store.dispatch(voohCampaignFormActions.setCardStatus({ basics: CardStatus.ERROR }))
        )
    }

    return api.voohCampaign
      .createVoohCampaign({
        agencyId: agency ? agency!.rawData!.id : null,
        name,
        briefName,
        startDate,
        endDate,
        sourceMediaFormat: clubMediaSource?.rawData?.type || VoohSourceMediaFormat.MOVE,
        brandId:
          clubMediaSource?.rawData?.type === VoohSourceMediaFormat.BRAND
            ? clubMediaSource?.rawData?.id
            : undefined,
        networkId:
          clubMediaSource?.rawData?.type === VoohSourceMediaFormat.NETWORK
            ? clubMediaSource?.rawData?.id
            : undefined,
      })
      .then(res => {
        if (!res.data) {
          return
        }

        const { id, mediaTree } = res.data.createVoohCampaign.voohCampaign

        store.dispatch(voohCampaignFormActions.setCardStatus({ basics: CardStatus.SAVED }))

        return { id, mediaTree }
      })
      .then((response: { id: string; mediaTree: VoohMediaNode[] } | undefined) => {
        store.dispatch(voohCampaignFormActions.setCardStatus({ basics: CardStatus.SAVED }))
        if (response) {
          navigate(Routes.VOOH_CAMPAIGNS.EDIT(response.id))
        }
      })
      .catch(
        () =>
          void store.dispatch(voohCampaignFormActions.setCardStatus({ basics: CardStatus.ERROR }))
      )
  }

  static submitDetailsValues = (currentVoohState: VoohCampaignForm): Promise<void> => {
    store.dispatch(voohCampaignFormActions.setCardStatus({ details: CardStatus.SAVING }))
    const { offer, mediaFormat, mediaDuration, organizationAttribute, hourFrequency } =
      currentVoohState.detailsValues
    return api.voohCampaign
      .updateVoohCampaignDetails({
        id: currentVoohState.id,
        mediaFormat,
        hourFrequency,
        offer,
        organizationAttribute,
        mediaDuration,
      })
      .then(res => {
        if (!res.data) {
          return
        }
        store.dispatch(
          voohCampaignFormActions.setVoohCampaignPublishable(
            res.data.updateVoohCampaignDetails.voohCampaign.publishable
          )
        )
        store.dispatch(voohCampaignFormActions.setCardStatus({ details: CardStatus.SAVED }))
      })
      .catch(() => {
        void store.dispatch(voohCampaignFormActions.setCardStatus({ details: CardStatus.ERROR }))
      })
  }

  static updateVoohMediaUsage = (
    campaignId: VoohCampaignForm['id'],
    usedMedia: VoohUsedMedia[]
  ): Promise<void> => {
    store.dispatch(voohCampaignFormActions.setCardStatus({ details: CardStatus.SAVING }))

    return api.voohCampaign
      .updateVoohCampaignMedia({
        campaignId,
        media: usedMedia,
      })
      .then(res => {
        if (!res.data) {
          return
        }
        store.dispatch(
          voohCampaignFormActions.setVoohCampaignPublishable(
            res.data.updateVoohCampaignMedia.voohCampaign.publishable
          )
        )
        store.dispatch(voohCampaignFormActions.setCardStatus({ details: CardStatus.SAVED }))
      })
      .catch(
        () =>
          void store.dispatch(voohCampaignFormActions.setCardStatus({ details: CardStatus.ERROR }))
      )
  }

  static updateCreation = (
    creation: VoohCreationUpdate,
    mediaFormat: VoohMediaFormat
  ): Promise<void> => {
    store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.SAVING }))
    const { id, details, duration, hourFrequency } = creation
    return api.voohCreation
      .updateVoohCampaignCreation({
        voohCreationId: id,
        details: details || '',
        mediaFormat,
        duration,
        hourFrequency,
      })
      .then(res => {
        if (!res.data) {
          store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.NONE }))
          return
        }

        store.dispatch(
          voohCampaignFormActions.setVoohCampaignPublishable(
            res.data.updateVoohCampaignCreation.voohCampaign.publishable
          )
        )

        if (res.data.updateVoohCampaignCreation.errors.length > 0) {
          store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.ERROR }))
        } else {
          store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.SAVED }))
        }
      })
      .catch(() => {
        void store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.ERROR }))
      })
  }

  static deleteVoohCreation = (creationId: VoohCreation['id']): Promise<void> => {
    store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.SAVING }))

    return api.voohCreation
      .deleteVoohCreation(creationId)
      .then(() => {
        store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.SAVED }))
        const voohForm = store.getState().voohCampaignForm.form
        store.dispatch(
          voohCampaignFormActions.updateForm({
            ...voohForm,
            creations: voohForm.creations.filter(formCreation => formCreation.id !== creationId),
          })
        )
      })
      .catch(
        () =>
          void store.dispatch(
            voohCampaignFormActions.setCardStatus({ creations: CardStatus.ERROR })
          )
      )
  }

  static archiveVoohCreation = (
    creationId: VoohCreation['id'],
    archiveReason: VoohCreation['archiveReason']
  ): Promise<void> => {
    store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.SAVING }))

    return api.voohCreation
      .archiveVoohCreation(creationId, archiveReason)
      .then((res: ArchiveVoohCreationFR) => {
        if (!res.data) {
          voohCampaignFormActions.setCardStatus({ creations: CardStatus.ERROR })
          return
        }
        store.dispatch(voohCampaignFormActions.setCardStatus({ creations: CardStatus.SAVED }))
        const voohForm = store.getState().voohCampaignForm.form
        store.dispatch(
          voohCampaignFormActions.updateForm({
            ...voohForm,
            creations: [
              ...voohForm.creations.filter(formCreation => formCreation.id !== creationId),
              res.data.archiveVoohCampaignCreation.voohCreation,
            ],
          })
        )
      })
      .catch(
        () =>
          void store.dispatch(
            voohCampaignFormActions.setCardStatus({ creations: CardStatus.ERROR })
          )
      )
  }

  static createVoohMediaTemplate = (
    name: string,
    mediaIds: VoohMedia['id'][]
  ): Promise<CreateVoohMediaTemplateFR> => {
    return api.voohCampaign.createVoohMediaTemplate(name, mediaIds)
  }

  static removeVoohMediaTemplate = (
    id: VoohMediaTemplate['id']
  ): Promise<FetchResult<{ removeVoohMediaTemplate: MutationStateResponse }>> => {
    return api.voohCampaign.removeVoohMediaTemplate(id)
  }

  static getVoohMediaTemplates = (voohMediaFormat: VoohMediaFormat): void => {
    store.dispatch(voohCampaignFormActions.setLaodingVoohMediaTemplates(true))
    api.voohCampaign
      .getVoohMediaTemplates(voohMediaFormat)
      .then(res => {
        if (res.data) {
          const mediaTemplates = res.data.voohMediaTemplates.nodes
          void store.dispatch(voohCampaignFormActions.setVoohMediaTemplates(mediaTemplates))
        }
      })
      .finally(() => {
        store.dispatch(voohCampaignFormActions.setLaodingVoohMediaTemplates(false))
      })
  }

  static getVoohCampaign = (campaignId: string): void => {
    void store.dispatch(
      voohCampaignFormActions.setCardStatus({
        basics: CardStatus.LOADING,
        details: CardStatus.LOADING,
        creations: CardStatus.LOADING,
      })
    )

    api.voohCampaign.getVoohCampaign(campaignId).then(res => {
      if (res.data) {
        const voohCampaign = res.data.node
        void store.dispatch(voohCampaignFormActions.setVoohCampaign(voohCampaign))
        void store.dispatch(
          voohCampaignRealizationActions.setTargetRealization(voohCampaign.details)
        )
      }
    })
  }

  static getVoohCampaignPublicReport = (uuid: string): void => {
    void store.dispatch(
      voohCampaignFormActions.setCardStatus({
        basics: CardStatus.LOADING,
        details: CardStatus.LOADING,
        creations: CardStatus.LOADING,
      })
    )

    api.voohCampaign.getVoohCampaignPublicReport(uuid).then(res => {
      if (res.data) {
        const voohCampaign = res.data.voohCampaignReportPublic.voohCampaign
        void store.dispatch(voohCampaignFormActions.setVoohCampaign(voohCampaign))
        void store.dispatch(
          voohCampaignRealizationActions.setTargetRealization(voohCampaign.details)
        )
      }
    })
  }

  static getVoohBrandsNetworks = (term: string): Promise<SelectAsyncValue<VoohBrandNetwork>[]> => {
    return api.voohCampaign.getVoohBrandsNetworks(term).then(res => {
      return res.data.voohBrandsNetworks.map(
        (brandNetwork): SelectAsyncValue<VoohBrandNetwork> => ({
          value: brandNetwork.id,
          label: brandNetwork.fullName,
          rawData: brandNetwork,
        })
      )
    })
  }

  static getUsersForSupervisors = (input: string): Promise<SelectAsyncValue<User>[]> =>
    api.user
      .getUsers({
        term: input,
        first: INPUT_LIST_LIMIT,
        sortColumn: UserSortColumn.FIRST_NAME,
        sortDirection: SortDirection.ASC,
        filters: { statuses: [UserStatus.ACTIVE, UserStatus.SUBSCRIPTION] },
      })
      .then(res => {
        return res.data.users.nodes.map(
          (u): SelectAsyncValue<User> => ({
            value: u.id,
            label: userFullName(u),
            rawData: u,
          })
        )
      })
      .catch(() => [])

  static getCompanies = (term: string): Promise<SelectAsyncValue<Company>[]> =>
    api.companies
      .getCompanies(term)
      .then(res =>
        res.data.companies.map(
          (company: Company): SelectAsyncValue<Company> => ({
            value: company.name,
            rawData: company,
          })
        )
      )
      .catch(() => [])

  static getAgencies = (inputValue: string): Promise<SelectAsyncValue<Agencies>[]> =>
    api.agency
      .getAgencies({
        term: inputValue,
        first: AGENCIES_LIMIT,
      })
      .then(res => {
        return res.data.agencies.nodes
          .filter((agency: Agency) => {
            const voohForm = store.getState().voohCampaignForm
            if (agency.default && voohForm.form.baseValues.agency === null) {
              store.dispatch(
                voohCampaignFormActions.setInitialAgency({
                  value: agency.name,
                  rawData: agency,
                  label: agency.name,
                })
              )
            }

            return false
          })
          .map(
            (agency: Agency): SelectAsyncValue<Agency> => ({
              value: agency.name,
              rawData: agency,
            })
          )
      })
      .catch(() => [])

  static validate = (
    values: VoohCampaignBasicValues,
    campaignStatus: CampaignStatus,
    t: TFunction,
    allowedFor: UseAuthorizeProps['allowedFor']
  ): VoohBasicFormErrors => {
    const hasStartAndEndDate = values.startDate.length && values.endDate.length
    const startDate = new Date(values.startDate)
    const endDate = new Date(values.endDate)

    const errors = {
      agency: [
        ...(allowedFor({ template: CAMPAIGN_FORM.AGENCY, status: campaignStatus })
          ? ErrorUtils.isRequired(values.agency?.value ?? '')
          : []),
      ],
      name: [
        ...ErrorUtils.isRequired(values.name || ''),
        ...ErrorUtils.maxLength(values.name || '', VOOH_CAMPAIGN_MAX_NAME_LENGTH),
      ],
      briefName: [
        ...ErrorUtils.isRequired(values.briefName || ''),
        ...ErrorUtils.maxLength(values.briefName || '', VOOH_CAMPAIGN_MAX_NAME_LENGTH),
      ],
      startDate: [
        ...(hasStartAndEndDate && DateUtils.isAfterDay(startDate, endDate)
          ? [t('form.startDateAfter')]
          : ErrorUtils.isRequired(values.startDate)),
      ],
      endDate: [
        ...(hasStartAndEndDate && DateUtils.isBeforeDay(endDate, startDate)
          ? [t('form.endDateBefore')]
          : ErrorUtils.isRequired(values.endDate)),
      ],
    }

    store.dispatch(voohCampaignFormActions.setErrors(errors))

    return errors
  }

  static isNotAllowedDate = (
    day: Date,
    dateTo: string,
    dateFrom: string,
    isDateFromDatePicker: boolean,
    reservationDate?: Date | null
  ): boolean => {
    if (reservationDate) {
      return DateUtils.isBeforeDay(day, new Date()) || DateUtils.isBeforeDay(day, reservationDate)
    }
    if (DateUtils.isBeforeDay(day, new Date())) {
      return true
    }
    if (isDateFromDatePicker) {
      return day > new Date(dateTo)
    }

    return day < new Date(dateFrom)
  }

  static hasErrors = (errors: VoohBasicFormErrors | undefined): boolean =>
    !VariableUtils.isDeepEqual(errors, VoohBasicFormErrorsEmpty)
}
